import {ComponentConfig} from '@mintdata/mint-kit/api/decorators/Component';
import {EditorConfig} from '@mintdata/mint-kit/api/decorators/Editor';
import {ContainerPropertyConfig} from '@mintdata/mint-kit/api/properties/ContainerPropertyConfig';
import {FunctionMap} from '@mintdata/mint-kit/api/properties/FunctionMap';
import {PropertyMap} from '@mintdata/mint-kit/api/properties/PropertyMap';
import {PropertySpec, PropName} from '@mintdata/mint-kit/api/properties/PropertySpec';

export class PropertyUtils
{
    private static FUNCTION_MAP_KEY = '___functionMap';
    private static PROPERTY_MAP_KEY = '___propertyMap';
    private static ATTACHED_PROPERTY_MAP_KEY = '___attachedPropertyMap';
    private static COMPONENT_CONFIG_KEY = '___componentConfig';
    private static EDITOR_CONFIG_KEY = '___editorConfig';
    private static CONTAINER_PROPERTY_CONFIG_KEY = '___containerPropertyConfig';

    public static findPropertyByName(propName:PropName, props:PropertySpec<any>[]):PropertySpec<any> | null
    {
        for( let i = 0; i < props.length; i++ )
        {
            const prop:PropertySpec<any> = props[i];

            if( prop && prop.name === propName )
                return prop;
        }

        return null;
    }

    public static getProperties(target:any, isAttached:boolean = false):Array<PropertySpec<any>>
    {
        const propertyMap:PropertyMap = PropertyUtils.getPropertyMap(target, isAttached);

        if( propertyMap )
            return propertyMap.getProperties();

        return [];
    }

    public static getPropertyMap(target:any, isAttached:boolean = false):PropertyMap
    {
        return target.constructor[isAttached ? PropertyUtils.ATTACHED_PROPERTY_MAP_KEY : PropertyUtils.PROPERTY_MAP_KEY];
    }

    public static getFunctionMap(target:any):FunctionMap
    {
        return target[PropertyUtils.FUNCTION_MAP_KEY];
    }

    public static setPropertyMap(target:any, propertyMap:PropertyMap, isAttached:boolean = false):void
    {
        target.constructor[isAttached ? PropertyUtils.ATTACHED_PROPERTY_MAP_KEY : PropertyUtils.PROPERTY_MAP_KEY] = propertyMap;
    }

    public static getOrCreatePropertyMap(target:any, isAttached:boolean = false):PropertyMap
    {
        const mapKey:string = isAttached ? PropertyUtils.ATTACHED_PROPERTY_MAP_KEY : PropertyUtils.PROPERTY_MAP_KEY;
        let propertyMap = target.constructor.hasOwnProperty(mapKey) && target.constructor[mapKey];

        if( !propertyMap )
        {
            let prototype:any = Object.getPrototypeOf(target);
            propertyMap = target.constructor[mapKey] = new PropertyMap();

            while( prototype )
            {
                const prototypePropertyMap = prototype.constructor.hasOwnProperty(mapKey) && prototype.constructor[mapKey];

                if( prototypePropertyMap )
                {
                    (prototypePropertyMap as PropertyMap).getProperties().forEach((property:PropertySpec<any>) =>
                    {
                        (propertyMap as PropertyMap).add(property);
                    });
                }

                prototype = Object.getPrototypeOf(prototype);
            }

        }

        return propertyMap;
    }

    public static getOrCreateFunctionMap(target:any):FunctionMap
    {
        const mapKey:string = PropertyUtils.FUNCTION_MAP_KEY;
        let functionMap = target[mapKey];

        if( !functionMap )
            functionMap = target[mapKey] = new FunctionMap();

        return functionMap;
    }

    public static getComponentConfig(target:any):ComponentConfig<any, any>
    {
        return target[PropertyUtils.COMPONENT_CONFIG_KEY];
    }

    public static setComponentConfig(target:any, componentConfig:ComponentConfig<any, any>):void
    {
        target[PropertyUtils.COMPONENT_CONFIG_KEY] = componentConfig;
    }

    public static getEditorConfig(target:any):EditorConfig
    {
        return target[PropertyUtils.EDITOR_CONFIG_KEY];
    }

    public static setEditorConfig(target:any, editorConfig:EditorConfig):void
    {
        target[PropertyUtils.EDITOR_CONFIG_KEY] = editorConfig;
    }

    public static getContainerPropertyConfig(target:any):ContainerPropertyConfig
    {
        return target[PropertyUtils.CONTAINER_PROPERTY_CONFIG_KEY];
    }

    public static setContainerPropertyConfig(target:any, containerPropertyConfig:ContainerPropertyConfig):void
    {
        target[PropertyUtils.CONTAINER_PROPERTY_CONFIG_KEY] = containerPropertyConfig;
    }
}
